import { useState } from 'react'
import { useRouteContext } from '@tanstack/react-router'
import { FileSubmission, FileStatus } from '@/models/file-submission.tsx'
import { useCandidateSubmissionErrors } from '@/queries.ts'
import Popup from '@/components/shared/Popup'
import Loader from '@/components/shared/Loader'
import Alert from '@/components/shared/Alert';
import Header from './Header';
import StatusSectionRejected from './StatusSectionRejected';
import StatusSectionOther from './StatusSectionOther';
import ErrorsSummaryList from '@/components/shared/ErrorSummaryList';
import ErrorsSection from './ErrorsSection';
import { getUniqueCount } from '@/utils/data-structures'
import { ColumnFiltersState } from '@tanstack/react-table'
import { type ErrorCode } from '@/models/error-codes.ts'

type FileRejectedPopupProps = {
    show: boolean;
    onClose: () => void;
    file: FileSubmission;
}

export default function FileStatusPopup({
    show,
    onClose,
    file
}: FileRejectedPopupProps) {
    const currentUserOrg = useRouteContext({ from: '/$leaid' }).currentUserOrg;
    
    const status = FileStatus.get(file.CurrentStatus.toString())?.Text;
    const [errorFilters, setErrorFilters] = useState<ColumnFiltersState>([])

    // Get errors
    const {
        data: errors,
        isLoading,
        error
    } = useCandidateSubmissionErrors(currentUserOrg.esiIdPath, file?.Id ?? '')

    // Get error records count from errors
    const errorRecordsCount = getUniqueCount(errors ?? [], 'ErrorRecordId');

    // Get count for each error code from errors
    const errorCodeCountsRecord = errors?.reduce((acc, error) => {
        acc[error.Code] = (acc[error.Code] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    // Convert errorCodeCountsRecord to array of ErrorCode objects to match ErrorsSummaryList props
    const errorCodeCounts: ErrorCode[] = Object.entries(errorCodeCountsRecord || {}).map(([code, count]) => ({
        code,
        count
    }));

    const applyErrorCodeFilter = (code: string) => {
        setErrorFilters(prev => [
            ...prev.filter(f => f.id !== 'Code'),
            { id: 'Code', value: code },
        ]);
    };

    return (
        <Popup
            show={show}
            onClose={onClose}
            header={<div className="fw-bold mb-2 small">{currentUserOrg.orgName}</div>}
            secondHeader={Header({ file })}
            buttonText="Close"
            className="dashboard-popup"
            onButtonClick={onClose}
        >
            {isLoading ? (
                <Loader containerClass="py-3" />
            ) : error ? (
                <Alert variant="danger" >
                    <p className="mb-0">An error occurred retrieving the record data.</p>
                </Alert>
            ) : 
                <>
                    {status === "Posted" && (
                        <StatusSectionOther 
                            file={file} 
                        />
                    )}
                    
                    {status === "Failed" && (
                        <StatusSectionOther 
                            file={file} 
                        />
                    )}

                    {status === "Rejected" && (
                        <div
                            className="d-grid h-100 gap-3"
                            style={{
                                gridTemplateColumns: '20rem 1fr',
                                gridTemplateRows: 'auto 1fr'
                            }}
                        >
                            <StatusSectionRejected 
                                file={file} 
                                errorRecordCount={errorRecordsCount} 
                            />
                            { errorCodeCounts && 
                                <div style={{ height: '22rem' }} className="border p-3 d-flex flex-column">
                                    <div className="mb-2">Error Counts by Code</div>
                                    <ErrorsSummaryList 
                                        errorCodes={errorCodeCounts}
                                        onErrorCodeClick={applyErrorCodeFilter}
                                    />
                                </div>
                            }
                            <div
                                className="overflow-auto" 
                                style={{ gridColumn: '1 / span 2' }}
                            >
                                { errors && 
                                    <ErrorsSection 
                                        errors={errors}
                                        columnFilters={errorFilters}
                                        onColumnFiltersChange={setErrorFilters}
                                    /> 
                                }
                            </div>
                        </div>
                    )}
                </>
            }
        </Popup>
    );
}